import * as React from 'react';
import { useState } from 'react';
import { makeStyles } from '@fluentui/react-components';
import CollapsibleFAQ from './CollapsibleFAQ';
import { FAQ_CONSTANTS } from '../constants/faqConstants';
import { useLocation } from '../context/LocationContext';

const useStyles = makeStyles({
  container: {
    maxWidth: '900px',
    margin: '4rem auto',
    padding: '0 1rem',
  },
  sectionTitle: {
    fontSize: '2.5rem',
    textAlign: 'center',
    color: 'var(--primary-color)',
    marginBottom: '1.5rem',
  },
  faqList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  muted: {
    fontSize: '18px',
    color: '#6a8ca3',
  },
  downloadLink: {
    display: 'inline-block',
    margin: '0.5rem 1rem 0.5rem 0',
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    color: '#fff8e1',
    textDecoration: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    selectors: {
      '&:hover': {
        backgroundColor: 'var(--primary-hover)',
        transform: 'translateY(-1px)',
      },
    },
  },
  downloadLinksContainer: {
    marginTop: '1rem',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
});

const FAQSection: React.FC = () => {
  const classes = useStyles();
  const [expandedFaqs, setExpandedFaqs] = useState<{[key: string]: boolean}>({});
  const { currentLocation } = useLocation();

  const toggleFaq = (faqKey: string) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [faqKey]: !prev[faqKey]
    }));
  };

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <section id={FAQ_CONSTANTS.SECTION_ID} className={classes.container} aria-labelledby={FAQ_CONSTANTS.TITLE_ID}>
      <h2 id={FAQ_CONSTANTS.TITLE_ID} className={classes.sectionTitle}>
        {FAQ_CONSTANTS.SECTION_TITLE}
      </h2>
      <div className={classes.faqList}>
        {/* Show Pune accommodation FAQ for Pune route or all/main routes */}
        {(currentLocation === 'pune' || currentLocation === 'all' || currentLocation === 'main') && (
          <CollapsibleFAQ 
            faqKey={FAQ_CONSTANTS.FAQS.ACCOMMODATION_IN_PUNE.KEY} 
            question={FAQ_CONSTANTS.FAQS.ACCOMMODATION_IN_PUNE.QUESTION}
            expandedFaqs={expandedFaqs}
            toggleFaq={toggleFaq}
          >
            <p className={classes.muted}>
              {FAQ_CONSTANTS.FAQS.ACCOMMODATION_IN_PUNE.ANSWER.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < FAQ_CONSTANTS.FAQS.ACCOMMODATION_IN_PUNE.ANSWER.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </CollapsibleFAQ>
        )}

        {/* Show Mumbai accommodation FAQ for Mumbai route, reception route, or all/main routes */}
        {(currentLocation === 'mumbai' || currentLocation === 'reception' || currentLocation === 'all' || currentLocation === 'main') && (
          <CollapsibleFAQ 
            faqKey={FAQ_CONSTANTS.FAQS.ACCOMMODATION_MUMBAI.KEY} 
            question={FAQ_CONSTANTS.FAQS.ACCOMMODATION_MUMBAI.QUESTION}
            expandedFaqs={expandedFaqs}
            toggleFaq={toggleFaq}
          >
            <p className={classes.muted}>
              {FAQ_CONSTANTS.FAQS.ACCOMMODATION_MUMBAI.ANSWER.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < FAQ_CONSTANTS.FAQS.ACCOMMODATION_MUMBAI.ANSWER.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </CollapsibleFAQ>
        )}

        <CollapsibleFAQ 
          faqKey={FAQ_CONSTANTS.FAQS.VENUES.KEY} 
          question={FAQ_CONSTANTS.FAQS.VENUES.QUESTION}
          expandedFaqs={expandedFaqs}
          toggleFaq={toggleFaq}
        >
          <p className={classes.muted}>
            {FAQ_CONSTANTS.FAQS.VENUES.ANSWER.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < FAQ_CONSTANTS.FAQS.VENUES.ANSWER.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        </CollapsibleFAQ>

        <CollapsibleFAQ 
          faqKey={FAQ_CONSTANTS.FAQS.ATTIRE.KEY} 
          question={FAQ_CONSTANTS.FAQS.ATTIRE.QUESTION}
          expandedFaqs={expandedFaqs}
          toggleFaq={toggleFaq}
        >
          <p className={classes.muted}>
            {FAQ_CONSTANTS.FAQS.ATTIRE.ANSWER.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < FAQ_CONSTANTS.FAQS.ATTIRE.ANSWER.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        </CollapsibleFAQ>

        <CollapsibleFAQ 
          faqKey={FAQ_CONSTANTS.FAQS.CONTACT.KEY} 
          question={FAQ_CONSTANTS.FAQS.CONTACT.QUESTION}
          expandedFaqs={expandedFaqs}
          toggleFaq={toggleFaq}
        >
          <p className={classes.muted}>
            {FAQ_CONSTANTS.FAQS.CONTACT.ANSWER.replace('{EMAIL}', FAQ_CONSTANTS.EMAIL).split('\n').map((line, index) => {
              if (line.includes(FAQ_CONSTANTS.EMAIL)) {
                const parts = line.split(FAQ_CONSTANTS.EMAIL);
                return (
                  <React.Fragment key={index}>
                    {parts[0]}
                    <a href={`mailto:${FAQ_CONSTANTS.EMAIL}`}>{FAQ_CONSTANTS.EMAIL}</a>
                    {parts[1]}
                    {index < FAQ_CONSTANTS.FAQS.CONTACT.ANSWER.split('\n').length - 1 && <br />}
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={index}>
                  {line}
                  {index < FAQ_CONSTANTS.FAQS.CONTACT.ANSWER.split('\n').length - 1 && <br />}
                </React.Fragment>
              );
            })}
          </p>
        </CollapsibleFAQ>

        <CollapsibleFAQ 
          faqKey={FAQ_CONSTANTS.FAQS.INVITE.KEY} 
          question={FAQ_CONSTANTS.FAQS.INVITE.QUESTION}
          expandedFaqs={expandedFaqs}
          toggleFaq={toggleFaq}
        >
          <div>
            <p className={classes.muted}>
              {FAQ_CONSTANTS.FAQS.INVITE.ANSWER}
            </p>
            <div className={classes.downloadLinksContainer}>
              {/* Show all PDFs for /all route */}
              {currentLocation === 'all' && (
                <>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.ENGAGEMENT.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.ENGAGEMENT.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.ENGAGEMENT.LABEL}
                  </a>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.LABEL}
                  </a>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.LABEL}
                  </a>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.LABEL}
                  </a>
                </>
              )}

              {/* Show Haldi, Wedding, and Reception PDFs for /main route */}
              {currentLocation === 'main' && (
                <>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.LABEL}
                  </a>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.LABEL}
                  </a>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.LABEL}
                  </a>
                </>
              )}

              {/* Show Haldi and Wedding PDFs for /pune route */}
              {currentLocation === 'pune' && (
                <>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.HALDI.LABEL}
                  </a>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.WEDDING.LABEL}
                  </a>
                </>
              )}

              {/* Show Engagement and Reception PDFs for /mumbai route */}
              {currentLocation === 'mumbai' && (
                <>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.ENGAGEMENT.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.ENGAGEMENT.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.ENGAGEMENT.LABEL}
                  </a>
                  <a 
                    href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.URL}
                    download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.FILENAME}
                    className={classes.downloadLink}
                    onClick={handleDownloadClick}
                  >
                    {FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.LABEL}
                  </a>
                </>
              )}

              {/* Show Reception PDF for /reception route */}
              {currentLocation === 'reception' && (
                <a 
                  href={FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.URL}
                  download={FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.FILENAME}
                  className={classes.downloadLink}
                  onClick={handleDownloadClick}
                >
                  {FAQ_CONSTANTS.FAQS.INVITE.PDFS.RECEPTION.LABEL}
                </a>
              )}
            </div>
          </div>
        </CollapsibleFAQ>
      </div>
    </section>
  );
};

export default FAQSection;